import React, { useState, useRef, useCallback } from 'react'

// Declarações globais para TypeScript
declare global {
  interface Window {
    SpeechRecognition: any
    webkitSpeechRecognition: any
  }
}

interface LocalVoiceRecorderProps {
  onTranscript: (text: string) => void
  disabled?: boolean
  className?: string
}

const LocalVoiceRecorder: React.FC<LocalVoiceRecorderProps> = ({
  onTranscript,
  disabled = false,
  className = ''
}) => {
  const [isRecording, setIsRecording] = useState(false)
  const [isSupported, setIsSupported] = useState(true)
  const recognitionRef = useRef<any>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const onTranscriptRef = useRef(onTranscript)
  const lastTranscriptRef = useRef('')
  
  // Atualizar ref sempre que onTranscript mudar
  React.useEffect(() => {
    onTranscriptRef.current = onTranscript
  }, [onTranscript])

  // Verificar suporte a Web Speech API (SEM dependência onTranscript)
  React.useEffect(() => {
    console.log('🎙️ Inicializando Web Speech API para LocalVoiceRecorder...')
    
    const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition
    
    if (!SpeechRecognition) {
      console.warn('❌ Web Speech API não é suportada neste navegador')
      setIsSupported(false)
      return
    }

    console.log('✅ Web Speech API disponível para LocalVoiceRecorder')
    setIsSupported(true)

    try {
      const recognition = new SpeechRecognition()
      recognition.continuous = true // Gravação contínua
      recognition.interimResults = true
      recognition.lang = 'pt-BR'
      recognition.maxAlternatives = 1

      recognition.onstart = () => {
        console.log('🎙️ LocalVoiceRecorder: Gravação iniciada')
        setIsRecording(true)
        lastTranscriptRef.current = '' // Limpar ref de duplicatas
      }

      recognition.onresult = (event: any) => {
        console.log('🎙️ LocalVoiceRecorder: Resultado recebido - Total results:', event.results.length)
        
        // PROCESSAR APENAS RESULTADOS FINAIS para evitar repetição
        let finalTranscript = ''
        let hasNewFinal = false
        
        // Processar apenas os novos resultados finais
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i]
          const transcript = result[0].transcript
          
          console.log(`🎙️ LocalVoiceRecorder Result ${i}:`, {
            isFinal: result.isFinal,
            transcript: transcript,
            confidence: result[0].confidence
          })
          
          if (result.isFinal) {
            finalTranscript += transcript
            hasNewFinal = true
          }
        }
        
        // Processar apenas se houver texto final novo
        if (hasNewFinal && finalTranscript.trim()) {
          const cleanTranscript = finalTranscript.trim()
          const lowerTranscript = cleanTranscript.toLowerCase()
          
          // Verificar duplicatas
          if (lastTranscriptRef.current === cleanTranscript) {
            console.log('🎙️ LocalVoiceRecorder: ⚠️ IGNORANDO duplicata:', cleanTranscript)
            return
          }
          lastTranscriptRef.current = cleanTranscript
          
          console.log('🎙️ LocalVoiceRecorder: ✅ PROCESSANDO RESULTADO FINAL:', cleanTranscript)
          
          // COMANDO ESPECIAL: PARAR DE ESCREVER
          if (lowerTranscript.includes('parar de escrever')) {
            console.log('🎙️ LocalVoiceRecorder: ⏹️ COMANDO: PARAR GRAVAÇÃO')
            // Parar a gravação
            if (recognitionRef.current) {
              try {
                recognitionRef.current.stop()
              } catch (e) {
                console.log('🎙️ LocalVoiceRecorder: Erro ao parar:', e)
              }
            }
            return // Não enviar o texto "parar de escrever" para o campo
          }
          
          // Para outros textos/comandos, enviar normalmente
          onTranscriptRef.current(cleanTranscript)
        } else {
          console.log('🎙️ LocalVoiceRecorder: ⏳ Aguardando resultado final... (ignorando intermediário)')
        }
      }

      recognition.onerror = (event: any) => {
        console.error('❌ LocalVoiceRecorder: Erro no reconhecimento:', event.error)
        setIsRecording(false)
        
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
        
        // Só mostrar alert para erros importantes, não para "aborted"
        if (event.error !== 'aborted') {
          let errorMessage = 'Erro no reconhecimento de voz local'
          switch (event.error) {
            case 'not-allowed':
              errorMessage = 'Permissão de microfone negada'
              break
            case 'no-speech':
              errorMessage = 'Nenhuma fala detectada'
              break
            case 'audio-capture':
              errorMessage = 'Erro ao capturar áudio'
              break
            case 'network':
              errorMessage = 'Erro de rede no reconhecimento'
              break
            default:
              console.log('🎙️ LocalVoiceRecorder: Erro silenciado:', event.error)
              return
          }
          
          alert(errorMessage)
        } else {
          console.log('🎙️ LocalVoiceRecorder: Reconhecimento abortado (normal)')
        }
      }

      recognition.onend = () => {
        console.log('🎙️ LocalVoiceRecorder: Gravação finalizada')
        
        // Se ainda deveria estar gravando (não foi parado manualmente), reiniciar
        if (isRecording && recognitionRef.current) {
          console.log('🎙️ LocalVoiceRecorder: Reiniciando gravação automática...')
          setTimeout(() => {
            if (isRecording && recognitionRef.current) {
              try {
                recognitionRef.current.start()
              } catch (e) {
                console.log('🎙️ LocalVoiceRecorder: Erro ao reiniciar:', e)
                setIsRecording(false)
              }
            }
          }, 100)
        } else {
          setIsRecording(false)
        }
        
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
      }

      recognitionRef.current = recognition
      console.log('✅ LocalVoiceRecorder: Speech Recognition configurado')
      
    } catch (error) {
      console.error('❌ LocalVoiceRecorder: Erro ao configurar:', error)
      setIsSupported(false)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort()
        } catch (error) {
          console.warn('LocalVoiceRecorder: Erro no cleanup:', error)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // onTranscript via ref para evitar re-inicializações

  const startRecording = useCallback(async () => {
    console.log('🎙️ LocalVoiceRecorder: Tentando iniciar gravação...')
    
    if (!isSupported) {
      console.error('❌ LocalVoiceRecorder: Web Speech API não suportada')
      alert('Reconhecimento de voz não é suportado neste navegador')
      return
    }
    
    if (disabled) {
      console.warn('⚠️ LocalVoiceRecorder: Botão desabilitado')
      return
    }
    
    if (isRecording) {
      console.warn('⚠️ LocalVoiceRecorder: Já está gravando')
      return
    }

    if (!recognitionRef.current) {
      console.error('❌ LocalVoiceRecorder: Speech Recognition não inicializado')
      return
    }

    try {
      // Solicitar permissão de microfone
      console.log('🎙️ LocalVoiceRecorder: Solicitando permissão...')
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      console.log('✅ LocalVoiceRecorder: Permissão concedida')
      
      // Parar o stream pois vamos usar Speech Recognition
      stream.getTracks().forEach(track => track.stop())
      
      console.log('🎙️ LocalVoiceRecorder: Iniciando Speech Recognition...')
      recognitionRef.current.start()
      
      // SEM TIMEOUT AUTOMÁTICO - só para com comando "parar de escrever" ou clique manual
      console.log('🎙️ LocalVoiceRecorder: Gravação contínua ativada - use "parar de escrever" ou clique no botão para parar')
      
    } catch (error) {
      console.error('❌ LocalVoiceRecorder: Erro ao iniciar:', error)
      setIsRecording(false)
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      
      let errorMessage = 'Erro ao acessar o microfone'
      if (error instanceof Error) {
        switch (error.name) {
          case 'NotAllowedError':
            errorMessage = 'Permissão de microfone negada'
            break
          case 'NotFoundError':
            errorMessage = 'Microfone não encontrado'
            break
          case 'NotReadableError':
            errorMessage = 'Microfone em uso por outro aplicativo'
            break
          default:
            errorMessage = error.message
        }
      }
      
      alert(errorMessage)
    }
  }, [isSupported, disabled, isRecording])

  const stopRecording = useCallback(() => {
    if (!isRecording) return

    try {
      console.log('⏹️ LocalVoiceRecorder: Parando gravação...')
      recognitionRef.current?.stop()
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    } catch (error) {
      console.error('❌ LocalVoiceRecorder: Erro ao parar:', error)
    }
  }, [isRecording])



  const handleClick = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  // Verificar suporte ao MediaRecorder
  React.useEffect(() => {
    const supported = 'MediaRecorder' in window && 
                     'mediaDevices' in navigator && 
                     'getUserMedia' in navigator.mediaDevices
    setIsSupported(supported)
  }, [])

  if (!isSupported) {
    return (
      <div className="text-muted small">
        🚫 Gravação não suportada
      </div>
    )
  }

  return (
    <button
      type="button"
      className={`btn btn-sm d-flex align-items-center ${
        isRecording 
          ? 'btn-danger' 
          : 'btn-light-primary'
      } ${disabled ? 'disabled' : ''} ${className}`}
      onClick={handleClick}
      disabled={disabled}
      title={isRecording ? 'Parar gravação local' : 'Gravar áudio localmente'}
      style={{ 
        whiteSpace: 'nowrap', 
        minHeight: '31px',
        minWidth: '45px',
        flexShrink: 0
      }}
    >
      {isRecording ? (
        <div className="d-flex align-items-center">
          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
          <i className="fas fa-stop"></i>
          <span className="d-none d-md-inline ms-1">Parar</span>
        </div>
      ) : (
        <>
          <i className="fas fa-microphone-alt me-1"></i>
          <span className="d-none d-lg-inline">Local</span>
        </>
      )}
    </button>
  )
}

export default LocalVoiceRecorder